/**
 * codec-mux-rules.js — shared codec ↔ container compatibility primitives.
 *
 * The primary class of FFmpeg WASM failures across our video tools is the
 * "Could not find tag for codec X in stream #N" muxer error: a source codec
 * (h264/HEVC video, AAC/MP3/PCM/AMR/WMA audio) stream-copied into a target
 * container (mp4/mov/m4v/webm) that has no codec_tag mapping for it. Pre-fix
 * (2026-06-24 sid ab0c72d7 + 31 events / 23 d cross-tool) the failure mode
 * was: every affected tool did `-c:v copy -c:a copy` first, exec'd FFmpeg
 * for ~30 s of input-probing wasted work, then bailed out at avformat_write_
 * header with the user-facing "FFmpeg exit code 1" toast. A few tools (video-
 * trim) already had a PROACTIVE codec-aware gate; this module extracts that
 * pattern into a single source of truth so every tool can use it.
 *
 * The gates return `true | false | null`:
 *   true  — codec is muxable into the target container with -c copy
 *   false — codec is NOT muxable; caller MUST encode (use *EncoderFor())
 *   null  — unknown codec or unknown output format; caller falls back to the
 *           reactive retry path it already has. NEVER assume true on null —
 *           that re-introduces the original bug.
 *
 * The codec name fed to the gates is FFmpeg's lower-case codec identifier
 * (e.g., "aac", "pcm_s16le", "h264", "hevc"). For mp4box.js probe sources,
 * use `audioCodecFromMp4Box(tracks)` to normalize the IETF/FourCC codec
 * string to FFmpeg's name before calling the gate.
 *
 * Usage (drop-in for the video-trim pattern):
 *   var rules = window.CodecMuxRules;
 *   var copyOk = rules.audioCopyOk(fileAudioCodec, outExt);
 *   if (copyOk === false) {
 *       args.push('-c:a', rules.audioEncoderFor(outExt), '-b:a', '128k');
 *   } else if (copyOk === true) {
 *       args.push('-c:a', 'copy');
 *   } else {
 *       // unknown — let the reactive retry catch a copy failure
 *       args.push('-c:a', 'copy');
 *   }
 */
(function(){
    'use strict';

    /* Audio codecs that mux cleanly into a given output container with -c:a
       copy. mp4/m4v/mov share the same QuickTime/ISOBMFF muxer rules. webm
       supports only the two Matroska/WebM-blessed codecs. mkv is the union
       (set to null = "anything goes" since matroska accepts ~everything).
       Unknown ext (avi, mp3, mka...) returns null from audioCopyOk → caller
       keeps existing behaviour. */
    var AUDIO_COPY_OK = {
        mp4:  ['aac', 'mp4a', 'ac3', 'eac3', 'alac'],
        /* 'opus' REMOVED from mp4/m4v (2026-09-08, support ticket #615 —
           the SAME argument as mp3 below, this time measured on Windows).
           A /slideshow-maker export (WebCodecs VP9 + stereo Opus in WebM)
           does not open on Windows at all; the /file-converter remux into
           .mp4 kept both streams and the .mp4 did not open either — while
           the very same VP9 with the audio re-encoded to AAC plays. Isolated
           through Windows Media Foundation (PresentationCore MediaPlayer),
           35 s real export, 08.09.2026:

               vp9 + (no audio)      .mp4   plays
               vp9 + aac             .mp4   plays
               h264 + opus (stereo)  .mp4   NOT opened  ← the audio alone kills it
               opus only (stereo)    .mp4   NOT opened
               vp9 + opus (stereo)   .mp4   NOT opened  ← what the copy produced
               vp9 + opus (mono)     .mp4   plays       ← the synthetic fixture
                                                          that made this look fine

           So Opus-in-MP4 is refused by the commonest desktop player for the
           commonest real-world Opus (stereo, from a browser AudioEncoder), and
           the refusal takes the whole file with it, picture included. A copy
           would be honest bytes-wise and still hand back exactly the dead file
           the person came here to escape. Re-encoding the audio to AAC costs
           about a second per minute of sound; the video stays a copy. */
        /* 'mp3' REMOVED from mp4/m4v (2026-08-02, KMPlayer report #2 from
           the same user): MP3-in-MP4 is ISO-legal and ffmpeg happily
           copies it, but the mp4 format card promises "plays everywhere
           (iPhone to TV)" and real players refuse it (KMPlayer twice,
           older TV splitters). A copy would be honest bytes-wise and
           still break the promise - so mp4-family outputs re-encode mp3
           to AAC. mov keeps mp3 (QuickTime's native container plays it
           broadly). The HW lane already re-encodes non-AAC since the
           mp4a.6b gate fix; this closes the FFmpeg -c:a copy lane. */
        m4v:  ['aac', 'mp4a', 'ac3', 'eac3', 'alac'],
        mov:  ['aac', 'mp4a', 'mp3', 'ac3', 'eac3', 'alac', 'pcm_s16be', 'pcm_s16le', 'pcm_s24be', 'pcm_s24le', 'pcm_s32be', 'pcm_s32le', 'pcm_f32be', 'pcm_f32le', 'pcm_f64be', 'pcm_f64le'],
        webm: ['opus', 'vorbis'],
        mkv:  null,
    };

    /* Video codecs that mux cleanly into a given output container with -c:v
       copy. mp4/m4v/mov accept the standard ISOBMFF codec_tags FFmpeg WASM
       knows (avc1/hvc1/mp4v/mjpa/etc.); webm = Matroska/WebM blessed codecs.
       UNKNOWN codecs MUST return false from videoCopyOk, never true —
       false costs a re-encode but always succeeds; true on an unsupported
       codec re-introduces the original tag-fail bug.

       ⚠️⚠️ `mpeg4` (DivX / MPEG-4 Part 2) and `mjpeg` were admitted here until
       2026-08-24 because the WASM 0.12.10 mp4 muxer really does register
       mp4v / mp4s codec_tags for them — tests/playwright/video-compressor-
       codec-mux.mjs s5 verified that the copy SUCCEEDS. It never asked
       whether the RESULT PLAYS, and it does not: measured in real Chromium,
       mpeg4-in-mp4 fires `loadeddata` with `videoWidth === 0` (AAC audio
       decodes, the picture is silently absent) and mjpeg-in-mp4 is refused
       outright with DEMUXER_ERROR_NO_SUPPORTED_STREAMS. So /video-trim handed
       a user a 70 MB `.mp4` that our own /split-screen, /video-crop and
       /video-compressor all rejected in turn — 25 minutes of a real session
       (sid 0bfeed92, 2026-08-12), ending in the bug report "не загружает
       файл". This is the SAME argument already accepted for mp3 above: the
       mp4 format card promises "plays everywhere", and a copy that is honest
       bytes-wise still breaks that promise. `mov` keeps both (QuickTime is
       their native home and plays them broadly), exactly like mp3.
       See videoPlaysInBrowsers() below for the general predicate. */
    var VIDEO_COPY_OK = {
        mp4:  ['h264', 'hevc', 'h265', 'av1', 'vp9'],
        m4v:  ['h264', 'hevc', 'h265'],
        mov:  ['h264', 'hevc', 'h265', 'prores', 'mjpeg', 'mpeg4'],
        webm: ['vp8', 'vp9', 'av1'],
        mkv:  null,
    };

    function _norm(s){ return String(s || '').toLowerCase().trim(); }

    /* ═══════ Есть ли у этого кодека декодер в браузерах ═══════
       `videoCopyOk` отвечает «влезет ли кодек в контейнер», `streamPlaybackRisk`
       — «переживёт ли аппаратный декодер ЭТИ СВОЙСТВА потока». Между ними была
       дыра: сам кодек может быть таким, что декодера нет НИ У КОГО, а мультиплексор
       его принимает молча и успешно. Тогда отказ не наступает — наступает мёртвый
       файл, и реактивная страховка «поймаем ошибку тега» не срабатывает никогда.

       Здесь перечислены кодеки, для которых ни один браузер не несёт декодер и
       которые при этом ЛЕГАЛЬНЫ в mp4/mov. Это наследие контейнеров AVI/WMV/FLV/
       VOB: их поток можно переложить в mp4 (`-c:v copy`) за секунды, и результат
       откроется в VLC — но не в браузере, не в нашем предпросмотре и ни в одном
       нашем соседнем туле.

       ⚠️ HEVC/AV1/VP9 сюда НЕ входят намеренно. Они тоже могут не декодироваться
       в КОНКРЕТНОМ браузере (HEVC на Chrome/Linux), но их несут телефоны и
       телевизоры, и файл с ними — полноценный. Граница проходит по «декодера нет
       нигде», а не по «этот браузер не смог»: судим по кодеку, а не по читателю
       (тот же принцип, что у streamPlaybackRisk ниже).

       Возвращает true / false / null — null значит «не знаем», и тогда решение
       остаётся за вызывающим, как и везде в этом модуле. */
    var NO_BROWSER_DECODER = {
        mpeg4: 1, msmpeg4v1: 1, msmpeg4v2: 1, msmpeg4v3: 1, mp4v: 1,
        mjpeg: 1, mjpegb: 1, jpeg: 1,
        mpeg1video: 1, mpeg2video: 1,
        vc1: 1, wmv1: 1, wmv2: 1, wmv3: 1, mss2: 1,
        dvvideo: 1, huffyuv: 1, ffv1: 1, cinepak: 1,
        theora: 1, flv1: 1, h263: 1, h263p: 1,
        svq1: 1, svq3: 1, rpza: 1,
        rv10: 1, rv20: 1, rv30: 1, rv40: 1,
        vp3: 1, vp5: 1, vp6: 1, vp6a: 1, vp6f: 1,
        indeo2: 1, indeo3: 1, indeo4: 1, indeo5: 1,
    };
    var BROWSER_DECODER_OK = { h264: 1, avc1: 1, hevc: 1, h265: 1, av1: 1, av01: 1, vp8: 1, vp9: 1, vp09: 1 };
    /* ═══════ Аргументы, которые обязана нести КОПИЯ потока ═══════
       -c:v copy переносит байты, но тег в контейнере пишет муксер, и для HEVC
       в семействе mp4 он по умолчанию пишет `hev1` — тег, которого требует
       ISO, но который Apple (QuickTime, Safari, iOS) не открывает: их
       документированное требование — `hvc1`. Замер 08.09.2026: Windows Media
       Foundation играет оба тега одинаково (три раунда), FFmpeg-WASM 5.1.4
       принимает -tag:v hvc1 на копии, так что смена тега ничего не стоит и
       ничего не ломает там, где мерилось. Вызывать ТОЛЬКО на копии: при
       перекодировании в libx264 тег не нужен и неверен. */
    function copyTagArgs(videoCodec, outFmt){
        var c = _norm(videoCodec), f = _norm(outFmt);
        if ((c === 'hevc' || c === 'h265') && (f === 'mp4' || f === 'm4v' || f === 'mov')) {
            return ['-tag:v', 'hvc1'];
        }
        return [];
    }

    /* ═══════ Какие потоки вообще брать ═══════
       Без -map ffmpeg берёт ОДНУ аудиодорожку («лучшую»), и фильм с русской
       и английской озвучкой молча теряет вторую — конвертер с чистым ✓ на
       выходе. `0:V:0` — первый НАСТОЯЩИЙ видеопоток (заглавная V исключает
       обложки-attached_pic), `0:a?` — все аудиодорожки, знак вопроса не
       роняет файл без звука. Субтитры и вложения НЕ мапятся: srt в mp4
       не копируется («Could not find tag for codec subrip»), и один такой
       поток валит весь вывод — замерено на MKV с srt. Проверено в WASM
       5.1.4: 2 аудио → обе в mp4, файл без аудио → ok, Windows играет.

       ⚠️⚠️ «Все аудиодорожки» — только те, у которых у НАШЕГО FFmpeg есть кодек.
       Пространственный звук iPhone (APAC, недавние iPhone пишут его по
       умолчанию рядом с обычным AAC, и бывает, что ПЕРВОЙ и default-дорожкой)
       сборка 5.1.4 называет `Audio: none (apac / 0x63617061)`: его нельзя ни
       декодировать («Decoder (codec none) not found for input stream #0:2»),
       ни скопировать («Could not find tag for codec none in stream #2»), а
       `0:a?` берёт его с собой — и падала КАЖДАЯ ступень лестницы обоих
       конвертеров (прод, 60 д: 40 из 59 упавших прогонов /video-converter,
       19 сессий — главный класс отказов тула; тот же `none` у `fpcm` и Vorbis
       в AVI). `audioStreams` — список дорожек глазами самого FFmpeg
       (window.__audioTracks.parse лога пробы, FFmpegProcessor.probeMeta):
       с ним нечитаемые дорожки не мапятся (`0:a:N` — порядковый номер среди
       аудио), без него или когда читаемы все — прежнее «все». */
    function streamMapArgs(audioStreams){
        var all = ['-map', '0:V:0', '-map', '0:a?'];
        var keep = readableAudioStreams(audioStreams);
        if (!keep || keep.length === audioStreams.length) return all;
        var out = ['-map', '0:V:0'];
        for (var i = 0; i < keep.length; i++) out.push('-map', '0:a:' + keep[i].a);
        return out;
    }

    /* Дорожки, которые FFmpeg может нести (null — судить не по чему). */
    function readableAudioStreams(audioStreams){
        if (!audioStreams || !audioStreams.length) return null;
        var keep = [];
        for (var i = 0; i < audioStreams.length; i++) {
            var t = audioStreams[i];
            if (t && typeof t.a === 'number' && t.codec && t.codec !== 'none') keep.push(t);
        }
        return keep;
    }

    /* Сколько дорожек streamMapArgs оставит за бортом — это число страница
       называет человеку: результат отличается от исходника. */
    function unreadableAudioCount(audioStreams){
        var keep = readableAudioStreams(audioStreams);
        return keep ? audioStreams.length - keep.length : 0;
    }

    /* FFmpeg отказал на дорожке, для которой у него нет кодека, — единственный
       отказ, который лечится более узкой картой потоков (см. streamMapArgs). */
    function isUnknownCodecTrackError(msg){
        return /\(codec none\) not found|Could not find tag for codec none/i.test(String(msg || ''));
    }

    function videoPlaysInBrowsers(videoCodec){
        var c = _norm(videoCodec);
        if (!c) return null;
        if (PARSE_ARTIFACTS.indexOf(c) !== -1) return null;
        if (NO_BROWSER_DECODER[c]) return false;
        if (BROWSER_DECODER_OK[c]) return true;
        return null;
    }

    /* ═══════ Копия, после которой картинки нет НИГДЕ, куда человек смотрит ═══════
       `videoPlaysInBrowsers` — вопрос про браузер. Этот — про плеер, которым человек
       откроет скачанный файл, и для большинства «небраузерных» кодеков ответ «да»:
       замер 20.10.2026 через Media Foundation (тот же Source Reader, что у «Кино и ТВ»,
       «Медиаплеера», «Фотографий», миниатюр Проводника; прибор
       tests/playwright/lib/mf-video-probe.ps1), FFmpeg 7.0, `-c:v copy`:

           mpeg4 · mpeg2video · mpeg1video  в .mp4      картинка есть
           mjpeg                            в .mp4      НЕТ: MF называет дорожку mp4v
                                                        (MPEG-4 Part 2) и не находит
                                                        декодер — 0xC00D5212
           mjpeg                     в .mov / .mkv / .avi   картинка есть (MJPG)

       Браузеры MJPEG не декодируют ни в каком контейнере, телефоны в mp4 — тоже, так
       что MJPEG, скопированный в mp4, показывает только VLC и его родня. Мультиплексор
       при этом принимает поток молча, а декод FFmpeg читает его чисто — ни одна
       проверка «по ошибке» его не ловит. Заявка на возврат №50 (19.10.2026):
       /repair-video выдал семь таких файлов с регистратора (MJPEG + PCM, AVI без
       заголовка, выбран «MP4») с вердиктом «восстановлено» — превью без картинки и тост
       «формат не поддерживается или файл повреждён». Решение «перекодировать» — за
       вызывающим (videoEncodeArgs); здесь только факт. */
    var PICTURELESS_COPY = {
        mp4: { mjpeg: 1, mjpegb: 1, jpeg: 1 },
        m4v: { mjpeg: 1, mjpegb: 1, jpeg: 1 },
    };
    function copyLosesPicture(videoCodec, outFmt){
        var t = PICTURELESS_COPY[_norm(outFmt)];
        return !!(t && t[_norm(videoCodec)]);
    }

    function audioCopyOk(audioCodec, outFmt){
        var c = _norm(audioCodec);
        if (!c) return null;
        var list = AUDIO_COPY_OK[_norm(outFmt)];
        if (list === undefined) return null;
        if (list === null) return true;
        return list.indexOf(c) !== -1;
    }

    /* FFmpeg's `wrapped_avframe` is a passthrough wrapper that appears in the
       OUTPUT stream description when `-f null -` is used as a no-op transcode
       (which probeStreaming does to extract metadata cheaply). It's NOT a
       real source codec — it's an FFmpeg internal artifact. Treating it as
       "unknown video codec" and returning false from videoCopyOk forced
       libx264 re-encode on what was actually a clean -c:v copy path, which
       in turn caused x264 malloc OOM on hi-res videos on low-RAM devices —
       the exact Bella #858 etalon bug (sid 73c4e1ea 2026-06-28). Returning
       null defers the gate decision (caller keeps -c:v copy, reactive retry
       at the tool's catch handler still covers genuine codec mismatches). */
    var PARSE_ARTIFACTS = ['wrapped_avframe', 'av_codec_default'];
    function videoCopyOk(videoCodec, outFmt){
        var c = _norm(videoCodec);
        if (!c) return null;
        if (PARSE_ARTIFACTS.indexOf(c) !== -1) return null;
        var list = VIDEO_COPY_OK[_norm(outFmt)];
        if (list === undefined) return null;
        if (list === null) return true;
        return list.indexOf(c) !== -1;
    }

    /* ═══════ Играется ли поток на обычном железе ═══════
       `videoCopyOk` отвечает на вопрос «влезет ли кодек в контейнер». Это НЕ то
       же самое, что «откроется ли файл у человека»: контейнер примет и High 10,
       и 4:4:4, а аппаратные декодеры телефонов, телевизоров и Windows Media
       Foundation — нет (0xC00D11B1, замерено 2026-07-10 на HDR-исходнике с
       iPhone; жалоба #1412 — тот же класс через 4:4:4).

       Отдельная функция нужна именно быстрым путям КОПИРОВАНИЯ: когда мы
       перекодируем, формат пикселей пришпиливает `enforceVideoOutputContract`,
       а при `-c copy` кодера нет и пришпиливать нечего — свойства исходника
       уезжают пользователю как есть.

       ⚠️ Возвращаем РИСК, а не запрет. Молча перекодировать 10 бит в 8 нельзя —
       это тихая потеря качества; решение (оставить как есть / сделать
       совместимую копию) принимает человек, а тул обязан его СПРОСИТЬ.
       ⚠️ Проверять «сможет ли ЭТОТ браузер декодировать» бесполезно: Chromium
       декодирует и 4:4:4, и 10 бит программно — предпросмотр играет, а плеер
       пользователя нет. Судим по свойствам потока, а не по браузеру. */
    var HW_SAFE_PIX = { yuv420p: 1, yuvj420p: 1, nv12: 1 };
    function streamPlaybackRisk(info){
        info = info || {};
        var pix = String(info.pixFmt || '').toLowerCase();
        var prof = String(info.profile || '').toLowerCase();
        if (!pix && !prof) return null;                 // не пробовали — не гадаем
        if (pix && HW_SAFE_PIX[pix]) return null;
        if (/10le|10be|p010/.test(pix) || /\b10\b|high 10/.test(prof)) return 'bit-depth-10';
        if (/444/.test(pix) || /4:4:4/.test(prof)) return 'chroma-444';
        if (/422/.test(pix) || /4:2:2/.test(prof)) return 'chroma-422';
        if (/^(rgb|bgr|gbr)/.test(pix)) return 'rgb';
        if (pix) return 'pix:' + pix;                   // незнакомое — сообщаем честно
        return null;
    }

    /* Encoder name to substitute when the gate returns false. For webm we
       prefer libopus (the universally-supported codec); everything else lands
       in MP4-family and gets AAC. */
    function audioEncoderFor(outFmt){
        return _norm(outFmt) === 'webm' ? 'libopus' : 'aac';
    }

    /* Video encoder substitute.

       ⛔ `libvpx-vp9` is UNUSABLE in our @ffmpeg/core@0.12.10 build: every
       invocation dies with `RuntimeError: memory access out of bounds`.
       Measured 2026-07-10 across CQ mode, bitrate mode, -threads 1, -row-mt 0,
       -deadline realtime, and frame sizes from 320x240 up to 1920x1080 — no
       argument dodges it. It cost /video-crop 21 failed exports a week, which
       was essentially every webm the FFmpeg path was ever asked for.

       ⭐ VP8 (`libvpx`) — the SAME library — works everywhere, at 1080p, and is
       the only encoder in this build that can carry ALPHA. So webm re-encodes
       land on VP8 here. Callers who only need an opaque webm should prefer
       WebMEncoder (WebCodecs VP9): ~2x faster and a better codec. */
    function videoEncoderFor(outFmt){
        return _norm(outFmt) === 'webm' ? 'libvpx' : 'libx264';
    }

    /* ── remuxPlan — copy-or-encode, decided once, for every tool ─────────
       Changing the CONTAINER is not the same thing as re-encoding. H.264
       inside an MKV is perfectly legal inside MP4, so "MKV → MP4" is a pure
       remux: measured on this WASM build (tests/playwright/
       add-music-remux-vs-reencode.mjs) an 8 s clip remuxes in 322 ms versus
       44 267 ms to re-encode — 137× — and the remux is lossless while CRF 23
       discarded 28 % of the payload.

       Two tools had independently written the SAME wrong rule, comparing
       extensions instead of asking whether the codec fits:
         · add-music-to-video: `outputFormat === 'same'` gated the copy path
           AND the UI hid the "original quality" option on any container
           change (Valentin, sid 548f5bee 2026-07-29 — a 63-min 791 MB MKV
           went to libx264, ≈5.8 h in WASM; he left after 35 min).
         · video-trim: `selectedFormat === originalExt` filtered "copy" out
           of the quality menu entirely, then two hardcoded webm overrides
           (`effectiveFormat` juggling) tried to patch the consequence —
           dead code, because the UI could never reach them. 137 re-encodes
           / 3 d on that tool versus 24 on add-music, so the bigger half of
           the cohort was in the tool nobody had connected to the report.

       Contract:
         srcCodec  ffmpeg codec id, '' when never probed
         srcExt    source container extension ('' if unknown)
         outExt    requested output container extension
         opts.userReencode   user explicitly asked for a quality/CRF preset
         opts.denyCopyInto   containers this caller cannot MUX (platform or
                             product constraint — e.g. our core cannot write
                             a usable WebM, so add-music passes ['webm'])
         opts.canRecoverFromCopyFailure
                             the caller's error path swaps `-c:v copy` for an
                             encoder when the mux is rejected. ONLY such a
                             caller may gamble on an unprobed codec.
       Returns { mode:'copy'|'encode', why:<reason> }. `why` is telemetry-
       grade: it names which branch fired, so a wrong table shows up as a
       number instead of a support ticket.

       On an UNPROBED codec the answer depends on that declared capability,
       and the reason it is a parameter rather than an assumption was measured:
       the first cut of this function was unconditionally optimistic because
       "every caller has a reactive tag-fail retry". /video-trim's retry only
       ever swapped the AUDIO codec, so MKV/VP8 → MP4 re-ran byte-identical
       args, failed twice and delivered NOTHING
       (tests/playwright/video-trim-container-remux.mjs caught it before it
       shipped). A caller that cannot recover gets the conservative answer;
       one that can gets the fast path plus a cheap failed header on the rare
       miss. Capability is declared, never presumed. */
    var REMUX_WHY = {
        USER: 'user_quality',
        DENIED: 'container_not_producible',
        SAME: 'same_container',
        LEGAL: 'codec_legal_in_target',
        UNKNOWN: 'codec_unknown_optimistic',
        UNKNOWN_NO_FALLBACK: 'codec_unknown_no_fallback',
        INCOMPATIBLE: 'codec_incompatible',
        NO_DECODER: 'codec_has_no_browser_decoder',
    };
    /* Контейнеры, чья вывеска обещает «откроется где угодно». Копировать в них
       поток без браузерного декодера — выдать мёртвый файл (см.
       videoPlaysInBrowsers). `mov` сюда не входит: QuickTime — родной дом
       mjpeg/mpeg4, и запрос именно .mov уважаем. */
    var UNIVERSAL_TARGETS = { mp4: 1, m4v: 1, webm: 1 };
    function remuxPlan(opts){
        opts = opts || {};
        var srcCodec = _norm(opts.srcCodec);
        var srcExt = _norm(opts.srcExt);
        var outExt = _norm(opts.outExt);
        if (opts.userReencode) return { mode: 'encode', why: REMUX_WHY.USER };
        var deny = opts.denyCopyInto;
        if (deny && deny.length) {
            for (var i = 0; i < deny.length; i++) {
                if (_norm(deny[i]) === outExt) return { mode: 'encode', why: REMUX_WHY.DENIED };
            }
        }
        /* Same container in and out — nothing to prove, the stream already
           lives in a file of exactly this type. */
        if (srcExt && outExt && srcExt === outExt) return { mode: 'copy', why: REMUX_WHY.SAME };
        /* Кодек, который не декодирует ни один браузер, мультиплексор в mp4
           принимает МОЛЧА и успешно — отказа, за который могла бы зацепиться
           реактивная страховка, не наступает. Поэтому решать надо здесь, до
           работы: перекодировать. Проверяется РАНЬШЕ videoCopyOk, потому что
           таблица отвечает на другой вопрос и для части этих кодеков (mjpeg,
           mpeg4 в mov) отвечает «да» совершенно правильно. */
        if (UNIVERSAL_TARGETS[outExt] && videoPlaysInBrowsers(srcCodec) === false) {
            return { mode: 'encode', why: REMUX_WHY.NO_DECODER };
        }
        var ok = videoCopyOk(srcCodec, outExt);
        if (ok === false) return { mode: 'encode', why: REMUX_WHY.INCOMPATIBLE };
        if (ok === true) return { mode: 'copy', why: REMUX_WHY.LEGAL };
        return opts.canRecoverFromCopyFailure
            ? { mode: 'copy',   why: REMUX_WHY.UNKNOWN }
            : { mode: 'encode', why: REMUX_WHY.UNKNOWN_NO_FALLBACK };
    }

    /* FFmpeg's probeStreaming uses `-f null -` which emits BOTH an "Input #0"
       block (real source description: "Video: h264 (High) (avc1 / ...)") AND
       an "Output #0, null, to '-'" block (passthrough: "Video: wrapped_avframe
       ...") — confirmed on Herd 2026-06-29. A naive .match() returns the FIRST
       Video:/Audio: line, which is what we want (input description). But
       per-line iterators that take the LAST match win the OUTPUT row and
       capture "wrapped_avframe" — the exact bug that broke add-music-to-
       video's codec gate and OOM'd Bella's 4K Yandex desktop. Anchor the
       parsers to the INPUT section to be defensive: stop scanning at the
       first "Output #" line we see. */
    function _inputOnly(log){
        if (!log) return '';
        var s = String(log);
        var cut = s.search(/^Output\s*#\d+/im);
        return cut > 0 ? s.substring(0, cut) : s;
    }
    function audioCodecFromFfmpeg(log){
        var m = _inputOnly(log).match(/Audio:\s*([a-z0-9_]+)/i);
        return m ? m[1].toLowerCase() : '';
    }
    function videoCodecFromFfmpeg(log){
        var m = _inputOnly(log).match(/Video:\s*([a-z0-9_]+)/i);
        return m ? m[1].toLowerCase() : '';
    }

    /* mp4box.js track[].codec strings come in IETF form (mp4a.40.2, ac-3,
       ec-3, alac, opus) for ISOBMFF and FourCC form (twos / sowt / in24 /
       ulaw / alaw / lpcm) for QuickTime PCM. Normalize to ffmpeg codec id
       so the same compatibility table works for both probe sources.
       Returns lowercase ffmpeg codec id or '' if no audio track found. */
    function audioCodecFromMp4Box(tracks){
        if (!tracks) return '';
        for (var i = 0; i < tracks.length; i++){
            var t = tracks[i];
            /* Don't filter on t.type === 'audio'. mp4box.js classifies
               QuickTime PCM (twos/sowt/in24/...) as type='metadata' because
               the QT sample description doesn't match its audio handler.
               Skip only obvious video tracks. */
            if (t.type === 'video') continue;
            var c = _norm(t.codec);
            /* ISO/IETF codec strings */
            if (c.indexOf('mp4a') === 0) return c === 'mp4a.6b' ? 'mp3' : 'aac';
            if (c === 'ac-3' || c === 'ac3') return 'ac3';
            if (c === 'ec-3' || c === 'eac3') return 'eac3';
            if (c.indexOf('alac') === 0) return 'alac';
            if (c.indexOf('opus') === 0) return 'opus';
            if (c.indexOf('vorbis') === 0) return 'vorbis';
            if (c.indexOf('mp3') === 0) return 'mp3';
            /* QuickTime/MOV FourCC strings (Apple devices, Pro Tools,
               screen recorders). Without explicit mapping mp4box returns
               the raw 4-char tag which isn't in any AUDIO_COPY_OK list, so
               proactive transcode still triggers — but `codec_detected`
               telemetry would carry an unrecognised codec name. Explicit
               mapping yields a clean ffmpeg id. */
            if (c === 'twos') return 'pcm_s16be';
            if (c === 'sowt') return 'pcm_s16le';
            if (c === 'in24') return 'pcm_s24be';
            if (c === '23ni') return 'pcm_s24le';
            if (c === 'in32') return 'pcm_s32be';
            if (c === '23in') return 'pcm_s32le';
            if (c === 'fl32') return 'pcm_f32be';
            if (c === 'fl64') return 'pcm_f64be';
            if (c === 'lpcm') return 'pcm_s16le';
            if (c === 'raw ' || c === 'raw') return 'pcm_u8';
            if (c === 'ulaw') return 'pcm_mulaw';
            if (c === 'alaw') return 'pcm_alaw';
            /* Not audio at all: QuickTime timecode, text / subtitle and hint
               tracks land here because they are neither 'video' nor 'audio'
               to mp4box. Returning 'tmcd' as the audio codec sent every
               camera .mov that carries a timecode track (and no audio, or
               the timecode before the audio) to the x264 lane as
               'audio_tmcd' — 5 sessions/30 d on /video-crop — and made
               hasAudio true for a silent file. */
            if (c === 'tmcd' || c === 'text' || c === 'sbtl' || c === 'subt' || c === 'clcp' || c === 'mp4s' || c === 'hint' || c === 'rtp ' || c === 'rtp') continue;
            /* Unknown — return raw tag so audioCopyOk returns false
               (won't match any whitelist) and proactive transcode fires.
               codec_detected telemetry carries the unknown string for
               future extension of this table. */
            return c || '';
        }
        return '';
    }

    /* mp4box.js video codec normalization — symmetric helper to the audio
       one. h264 codecs come as 'avc1.42E028' style; HEVC as 'hev1.*' /
       'hvc1.*'; AV1 as 'av01.*'; VP9 as 'vp09.*' (or just 'vp9').

       ⚠️⚠️ Do NOT filter on `t.type === 'video'` — the audio sibling above says
       the same thing and for the same reason, and the video side did it anyway
       until 2026-08-24. mp4box classifies a QuickTime MJPEG track as
       type='metadata' (measured: `{type:'metadata', codec:'jpeg'}`), and 3GP
       H.263 likewise (`{type:'metadata', codec:'s263'}`). Both returned '' —
       "codec unknown" — so remuxPlan fell to its optimistic copy and an
       MOV/MJPEG trimmed to .mp4 came out dead exactly like the AVI case this
       whole rule exists for. It also cost the caller the 6-SECOND empirical
       probe (measured: 6007 ms and 6009 ms) where a container read answers in
       ~70 ms, because a file that neither errors nor presents a frame can only
       be judged by timeout. Skip AUDIO tracks instead, by type and by codec. */
    var MP4BOX_AUDIO_TAGS = {
        twos:1, sowt:1, in24:1, '23ni':1, in32:1, '23in':1, fl32:1, fl64:1,
        lpcm:1, 'raw ':1, raw:1, ulaw:1, alaw:1, samr:1, sawb:1, 'ac-3':1,
        'ec-3':1, alac:1, opus:1, vorbis:1, mp3:1, dtse:1, dtsc:1,
    };
    function videoCodecFromMp4Box(tracks){
        if (!tracks) return '';
        for (var i = 0; i < tracks.length; i++){
            var t = tracks[i];
            if (t.type === 'audio' || t.type === 'subtitles' || t.type === 'text') continue;
            var c = _norm(t.codec);
            if (!c) continue;
            if (c.indexOf('mp4a') === 0 || MP4BOX_AUDIO_TAGS[c]) continue;
            if (c.indexOf('avc1') === 0 || c.indexOf('avc3') === 0) return 'h264';
            if (c.indexOf('hev1') === 0 || c.indexOf('hvc1') === 0) return 'hevc';
            if (c.indexOf('av01') === 0) return 'av1';
            if (c.indexOf('vp09') === 0 || c.indexOf('vp9') === 0) return 'vp9';
            if (c.indexOf('vp8') === 0) return 'vp8';
            if (c.indexOf('mp4v') === 0 || c === 'mp4s') return 'mpeg4';
            /* QuickTime / 3GP FourCCs. Legal in an ISOBMFF box, decoded by no
               browser — the exact population videoPlaysInBrowsers() judges. */
            if (c === 'jpeg' || c === 'mjpa' || c === 'mjpb' || c === 'dmb1') return 'mjpeg';
            if (c === 's263' || c === 'h263' || c === 'd263') return 'h263';
            if (c === 'svq1') return 'svq1';
            if (c === 'svq3') return 'svq3';
            if (c === 'cvid') return 'cinepak';
            if (c === 'rpza') return 'rpza';
            if (c.indexOf('dvc') === 0 || c === 'dvpp' || c === 'dv5n' || c === 'dv5p') return 'dvvideo';
            if (c.indexOf('apc') === 0 || c.indexOf('ap4') === 0) return 'prores';
            return c || '';
        }
        return '';
    }

    /* Detect whether an FFmpeg error message indicates a codec_tag muxer
       failure (the class this module exists to prevent). Reactive retry
       paths in tools call this to decide whether to switch from -c copy to
       full re-encode. Matches the exact stderr fragments observed in prod
       across the 31-event cohort. */
    var TAG_FAIL_RE = /Could not find tag for codec|codec not currently supported in container|Could not write header for output file/i;
    function isTagFailError(msg){
        return TAG_FAIL_RE.test(String(msg || ''));
    }

    /* Extract the failing codec name + stream-index from a tag-fail error
       message. Used by reactive retry to decide whether to swap -c:v copy,
       -c:a copy, or both. Returns { codec, streamIdx } or null. */
    function parseTagFail(msg){
        if (!msg) return null;
        var m = String(msg).match(/Could not find tag for codec\s+([a-z0-9_]+)\s+in stream\s+#(\d+)/i);
        if (!m) return null;
        return { codec: m[1].toLowerCase(), streamIdx: parseInt(m[2], 10) };
    }

    /* ════════════════════════════════════════════════════════════════════
       OUTPUT CONTRACT
       ════════════════════════════════════════════════════════════════════
       The tables above answer "may this stream be copied into that
       container". This section answers the second half of the same
       question: "if we re-encode, what must come out".

       An output is only good when a consumer player opens it, and FFmpeg's
       libx264 does NOT guarantee that on its own — it inherits the decoded
       pixel format. A 10-bit source (every HDR iPhone .mov since iOS 13)
       therefore yields H.264 **High 10**, which Windows Media Foundation
       refuses with 0xC00D11B1 → «неподдерживаемые параметры кодировки».

       The trap is that this is invisible in-browser: Chromium decodes
       High 10 in software, so the result preview plays perfectly and no
       error telemetry fires. Only the user's desktop player fails. Verified
       2026-07-10 by driving the real /video-crop UI in the reporter's exact
       YaBrowser 26.6 build on a real iPhone HLG .mov, then opening the
       downloaded file through Media Foundation.

       Measured, not assumed (tests/playwright/video-crop-contract.mjs):
         · `-pix_fmt yuv420p` alone is sufficient and necessary against
           High 10. It also covers -filter_complex graphs, where a trailing
           `format=yuv420p` in -vf would never be reached.
         · For HDR sources `-pix_fmt` alone opens the file but leaves SDR
           pixels wearing bt2020/HLG tags — mean RGB drifts G+25 / B+13 off
           the reference. The tone-map chain restores it to within 0.1.
         · The explicit `-color_primaries/-color_trc/-colorspace bt709`
           flags are REDUNDANT: zscale already writes those tags. Dropped.
         · The tone-map chain costs 2.26× wall-clock, so it is gated on a
           real HDR transfer characteristic — never applied blindly. It also
           hard-fails on sources with no declared transfer (`zscale=t=linear`
           cannot guess), which is a second reason the gate is mandatory. */

    /* Canonical HDR→SDR chain — the FFmpeg lanes' half of the HDR → SDR door
       (hdr-to-sdr.js owns the target, docs/hdr-to-sdr.md): reference white
       203 nits = SDR white (BT.2408), Möbius roll-off on max(R,G,B) from 0.9
       of white to the 1000-nit peak (4.926 whites), bt709 tv-range, 8-bit.
       zscale decodes HLG as scene light, so this is the nearest built-in chain
       to the WebGL lanes' display-light target: equal mean luma, 33–42 dB
       (measured 2026-10-22). The Hable chain it replaces (npl=100) put
       reference white at ~0.8 — every FFmpeg-lane iPhone file came out dimmer
       than the WebGL lane's (which was itself ~40 % too dark until the door).
       Insert into -vf / -filter_complex AFTER geometry, BEFORE the encoder. */
    var HDR_TO_SDR_VF = 'zscale=t=linear:npl=203,format=gbrpf32le,zscale=p=bt709,' +
                        'tonemap=tonemap=mobius:param=0.9:desat=0:peak=4.926,zscale=t=bt709:m=bt709:r=tv,format=yuv420p';

    function hdrToSdrFilter(){ return HDR_TO_SDR_VF; }

    /* Video encoder args that satisfy the output contract for `outFmt`.
       Callers prepend their own geometry/rate args (-vf, -vsync, …).
       `opts.alpha` keeps a transparent VP9 output transparent — pinning
       yuv420p there would silently flatten it (circle-crop → webm). H.264
       has no alpha, so the flag is ignored for mp4. */
    function videoEncodeArgs(outFmt, opts){
        opts = opts || {};
        var crf = String(opts.crf == null ? 23 : opts.crf);
        if (_norm(outFmt) === 'webm') {
            /* VP8, never VP9 — see videoEncoderFor for why. VP8 has no
               `-b:v 0` constant-quality mode: -crf needs a rate cap to bound
               against. `opts.alpha` asks for transparency, which VP8 alone can
               deliver in this stack (H.264 has none; WebCodecs answers "Alpha
               encoding is not currently supported" for every codec). It
               additionally REQUIRES `-auto-alt-ref 0` — without it the encoder
               refuses to open with "Error initializing output stream".
               Note the alpha plane lives in the container's BlockAdditional,
               so ffprobe reports pix_fmt=yuv420p even on a transparent file;
               decode with `-c:v libvpx` to see it. */
            /* The rate cap must be GENEROUS or it, not -crf, decides quality.
               Measured on 640x480: `-b:v 0` silently collapses to ffmpeg's 200k
               default (61 KB, 37.4 dB) while 2M / 6M / 20M all yield the same
               256 KB at 45.8 dB — i.e. the cap stops binding at ~2M and -crf
               governs. 12M keeps it non-binding up to 1080p. NEVER pass 0. */
            var v = ['-c:v', 'libvpx', '-crf', crf, '-b:v', opts.bitrate || '12M'];
            if (opts.alpha) v.push('-auto-alt-ref', '0');
            v.push('-pix_fmt', opts.alpha ? 'yuva420p' : 'yuv420p');
            return v;
        }
        return ['-c:v', 'libx264', '-preset', opts.preset || 'veryfast',
                '-crf', crf, '-pix_fmt', 'yuv420p']
            .concat(rateCapArgs(opts.srcBitrateBps, opts));
    }

    /* ── Source-aware rate ceiling (shared with the hardware lane) ──────────
       `-crf` alone spends whatever the quality target wants, regardless of how
       light the source was: MEASURED on a 1 Mbps 1080p clip, the FFmpeg lane
       turned 27.6 MB into 71.5 MB (x2.6) at "same as source", and user 4126's
       real export went 25 MB -> 101 MB (x4). The WebCodecs lane has capped this
       since its bitrate work (`bitrate_cap_applied`: ceiling = 1.3x source), so
       the two engines disagreed about what "same as source" means — the output
       size depended on which engine happened to win. Same formula, both lanes.

       Only applied when the caller states the source bitrate AND asks to match
       it; an explicit high/medium/low target is the user asking for a quality,
       not for the source's size. bufsize = 2x maxrate is the standard VBV
       window — tighter makes x264 oscillate, looser stops constraining. */
    function rateCapArgs(srcBitrateBps, opts){
        opts = opts || {};
        var src = +srcBitrateBps;
        if (!isFinite(src) || src <= 0) return [];
        var growth = +opts.maxGrowth > 0 ? +opts.maxGrowth : 1.3;
        var FLOOR = 1000000;                       // never cap below 1 Mbps…
        var ceiling = Math.max(Math.min(FLOOR, src), Math.floor(src * growth));
        // …unless the source itself was lighter than that (same lowerBound rule
        // the hardware lane uses, so a 0.5 Mbps source is not forced up to 1).
        return ['-maxrate', Math.round(ceiling / 1000) + 'k',
                '-bufsize', Math.round(ceiling * 2 / 1000) + 'k'];
    }

    /* ── The output contract, enforced on an already-built command ──────────
       `videoEncodeArgs` above is the front door: a caller that uses it cannot
       get this wrong. But 37 call sites across 16 blades hand-roll their own
       `['-c:v','libx264','-preset',…]`, several assembled dynamically — a
       `.map()` that swaps `copy`→`libx264`, a `.splice()` that rewrites the
       encoder in place. Asking each of them to remember `-pix_fmt` is how the
       class survived in the first place: libx264 with no pixel format inherits
       the SOURCE's, so a 10-bit input yields H.264 **High 10**, which Windows
       refuses (0xC00D11B1) while Chromium decodes it in software — hiding the
       bug from the tool, from the result preview and from telemetry alike.

       So the rule is also applied at the boundary, to whatever command is about
       to reach FFmpeg. Conservative on purpose:
         · only when an 8-bit-only encoder is actually selected;
         · never when the caller already stated a pixel format — an explicit
           `yuva420p` (VP8 alpha) or `yuv422p` must survive untouched;
         · `-vf …,format=yuv420p` does NOT count as a statement: it lands at the
           filter tail, a later `scale` can undo it, and `-pix_fmt` is the only
           thing the encoder itself listens to.
       Returns a NEW array; never mutates the caller's. */
    var PIN_8BIT = { libx264: 1, libx265: 1 };

    function enforceVideoOutputContract(args){
        if (!Array.isArray(args) || !args.length) return args;
        if (args.indexOf('-pix_fmt') !== -1) return args;
        for (var i = 0; i < args.length - 1; i++) {
            if (!/^-c:v(:\d+)?$/.test(String(args[i]))) continue;
            if (!PIN_8BIT[String(args[i + 1])]) continue;
            var out = args.slice();
            out.splice(i + 2, 0, '-pix_fmt', 'yuv420p');
            return out;
        }
        return args;
    }

    /* Audio args: copy when the source codec is legal in the target
       container, otherwise transcode with the container's native encoder.
       `srcAudioCodec` comes from audioCodecFromFfmpeg / audioCodecFromMp4Box.
       An empty/unknown codec means "we never probed" — transcode, because a
       blind `-c:a copy` is exactly how pcm_s16le kills a .mov→.mp4 export
       ("Could not find tag for codec pcm_s16le", 6 occurrences/week). */
    function audioMuxArgs(srcAudioCodec, outFmt, opts){
        opts = opts || {};
        /* Substituting a sentinel for "we never probed" gets the two cases
           right in one call: a permissive container (mkv, copy-list `null`)
           still copies — lossless and free — while a strict one (mp4) finds
           no match and transcodes, which is what a blind `-c:a copy` should
           have done all along. */
        if (audioCopyOk(srcAudioCodec || 'unknown', outFmt) === true) return ['-c:a', 'copy'];
        return ['-c:a', audioEncoderFor(outFmt), '-b:a', opts.bitrate || '128k'];
    }

    /* One structured read of an `ffmpeg -i` stderr log. Every tool used to
       re-derive these with its own regexes; the rotation and colour fields in
       particular were simply missing, so tools built their workspace from
       CODED dimensions. For a rotated portrait iPhone clip the log prints
       1920x1080 while the displayed (and post-autorotate) frame is 1080x1920 —
       crop rectangles then overflow the frame and FFmpeg dies with
       "Error reinitializing filters!". `width`/`height` below are DISPLAY
       dimensions; `codedWidth`/`codedHeight` keep the raw values. */
    function probeFromFfmpeg(log){
        var s = _inputOnly(log);
        var out = {
            videoCodec: videoCodecFromFfmpeg(log),
            audioCodec: audioCodecFromFfmpeg(log),
            hasAudio: /Stream[^\n]*Audio:/i.test(s),
            duration: 0, rotation: 0,
            width: 0, height: 0, codedWidth: 0, codedHeight: 0,
            transfer: '', primaries: '', isHdr: false, bitDepth: 0
        };
        var d = s.match(/Duration:\s*(\d+):(\d+):(\d+(?:\.\d+)?)/);
        if (d) out.duration = (+d[1]) * 3600 + (+d[2]) * 60 + parseFloat(d[3]);

        var r = s.match(/Stream[^\n]*Video:[^\n]*?(\d{2,5})x(\d{2,5})/i);
        if (r) { out.codedWidth = +r[1]; out.codedHeight = +r[2]; }

        /* Two spellings across FFmpeg generations: the modern side-data line
           `displaymatrix: rotation of -90.00 degrees` and the legacy metadata
           `rotate : 90`. Sign convention differs; only the axis matters here. */
        var rot = s.match(/rotation of\s*(-?\d+(?:\.\d+)?)\s*degrees/i) ||
                  s.match(/^\s*rotate\s*:\s*(-?\d+)/im);
        if (rot) out.rotation = ((Math.round(parseFloat(rot[1])) % 360) + 360) % 360;

        var swap = out.rotation === 90 || out.rotation === 270;
        out.width  = swap ? out.codedHeight : out.codedWidth;
        out.height = swap ? out.codedWidth  : out.codedHeight;

        var tr = s.match(/(?:color_transfer|colortransfer)[:=]\s*([a-z0-9\-]+)/i);
        if (tr) out.transfer = tr[1].toLowerCase();
        var pr = s.match(/(?:color_primaries|colorprimaries)[:=]\s*([a-z0-9\-]+)/i);
        if (pr) out.primaries = pr[1].toLowerCase();
        var pf = s.match(/Video:[^\n]*?\b(yuv[a-z0-9]*?(\d{1,2})le|yuv4\d\dp)\b/i);
        if (pf) out.bitDepth = pf[2] ? +pf[2] : 8;

        /* HDR = a real HDR transfer characteristic (PQ or HLG), or bt2020
           primaries with no recognised transfer. 10-bit alone is NOT enough —
           10-bit bt709 SDR exists, and tone-mapping it costs 2.26x and washes
           the picture. `bt2020-10` / `bt2020-12` are the wide-gamut SDR
           curves: not HDR. Same verdict the mp4box `colr` reader gives in
           webcodecs-pipeline.js — the two probe sources must never disagree. */
        var HDR_TRC = /^(smpte2084|arib-std-b67)$/;
        var SDR_TRC = /^(bt709|smpte170m|smpte240m|bt470bg|gamma22|gamma28|bt2020-10|bt2020-12|iec61966-2-1|linear|log|log100)$/;
        out.isHdr = HDR_TRC.test(out.transfer) ||
                    (out.primaries === 'bt2020' && !SDR_TRC.test(out.transfer));
        return out;
    }

    /* Same verdict from a WebCodecsPipeline.probeCodec() result (mp4box —
       no 30 MB FFmpeg download needed for the mp4 family). Kept here so both
       probe sources answer through one predicate. */
    function isHdrFromProbe(probe){
        if (!probe) return false;
        return !!probe.isHdr;
    }

    window.CodecMuxRules = {
        audioCopyOk: audioCopyOk,
        videoCopyOk: videoCopyOk,
        videoPlaysInBrowsers: videoPlaysInBrowsers,
        copyLosesPicture: copyLosesPicture,
        audioEncoderFor: audioEncoderFor,
        videoEncoderFor: videoEncoderFor,
        audioCodecFromFfmpeg: audioCodecFromFfmpeg,
        videoCodecFromFfmpeg: videoCodecFromFfmpeg,
        audioCodecFromMp4Box: audioCodecFromMp4Box,
        videoCodecFromMp4Box: videoCodecFromMp4Box,
        isTagFailError: isTagFailError,
        parseTagFail: parseTagFail,
        /* copy-or-encode decision (shared by add-music-to-video, video-trim) */
        remuxPlan: remuxPlan,
        _REMUX_WHY: REMUX_WHY,
        /* output contract */
        enforceVideoOutputContract: enforceVideoOutputContract,
        streamPlaybackRisk: streamPlaybackRisk,
        hdrToSdrFilter: hdrToSdrFilter,
        videoEncodeArgs: videoEncodeArgs,
        rateCapArgs: rateCapArgs,
        audioMuxArgs: audioMuxArgs,
        probeFromFfmpeg: probeFromFfmpeg,
        isHdrFromProbe: isHdrFromProbe,
        /* expose the tables for tests / debug. Treat read-only. */
        copyTagArgs: copyTagArgs,
        streamMapArgs: streamMapArgs,
        unreadableAudioCount: unreadableAudioCount,
        isUnknownCodecTrackError: isUnknownCodecTrackError,
        _AUDIO_COPY_OK: AUDIO_COPY_OK,
        _VIDEO_COPY_OK: VIDEO_COPY_OK,
        _NO_BROWSER_DECODER: NO_BROWSER_DECODER,
        _HDR_TO_SDR_VF: HDR_TO_SDR_VF,
    };
})();
